/**
 * Cash Drawer Reconciliation panel — Finance > "Cash drawer" tab.
 *
 * Surfaces every offline-settled refund (refund_method='offline') logged
 * within the selected window, broken down by settlement method so the
 * front-desk staff can match the report against the physical till.
 *
 * Reads `/api/fees/cash-drawer-reconciliation` exclusively.  Self-hides
 * gracefully on auth errors (the endpoint is owner-gated).
 */
import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Banknote, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import React from 'react';

const DAYS_OPTIONS = [1, 7, 30];

const METHOD_LABEL = {
  cash: 'Cash',
  upi: 'UPI direct',
  cash_or_upi: 'Cash or UPI',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
};

function formatINR(n) {
  if (n === null || n === undefined) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) {
    return iso;
  }
}

export default function CashDrawerReconciliationTab() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/fees/cash-drawer-reconciliation', { params: { days } });
      setData(res?.data || null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not load cash-drawer report.';
      setError(detail);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  return (
    <div className="space-y-4" data-testid="cash-drawer-tab">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0 gap-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Banknote className="h-4 w-4 text-emerald-600" />
              Cash drawer reconciliation
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Offline-settled refunds for matching against your physical till at end-of-day. Live Razorpay refunds are not included.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Window</Label>
            {/* Build via React.createElement so source-map dev instrumentation
                doesn't inject a <span> wrapper as a <select> child. */}
            {React.createElement(
              'select',
              {
                value: days,
                onChange: (e) => setDays(Number(e.target.value)),
                className: 'h-8 rounded-md border border-input bg-background px-2 text-xs',
                'data-testid': 'cash-drawer-window-select',
              },
              ...DAYS_OPTIONS.map((d) =>
                React.createElement('option', { key: d, value: d }, d === 1 ? 'Today' : `Last ${d} days`)
              )
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={fetchReport}
              disabled={loading}
              data-testid="cash-drawer-refresh"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50/40 dark:border-red-900 dark:bg-red-950/20">
          <CardContent className="pt-4 text-xs text-red-700 dark:text-red-300" data-testid="cash-drawer-error">
            {error}
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cash-drawer-summary">
        <Card>
          <CardContent className="pt-4">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">Total refunded</div>
            <div className="text-2xl font-extrabold text-slate-900 dark:text-slate-50 tabular-nums" data-testid="cash-drawer-grand-total">
              {formatINR(data?.grand_total)}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-testid="cash-drawer-grand-count">
              {data?.grand_count || 0} {data?.grand_count === 1 ? 'refund' : 'refunds'}
            </div>
          </CardContent>
        </Card>
        {(data?.method_summary || []).map((m) => (
          <Card key={m.method} data-testid={`cash-drawer-method-${m.method}`}>
            <CardContent className="pt-4">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                {METHOD_LABEL[m.method] || m.method}
              </div>
              <div className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
                {formatINR(m.total)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {m.count} {m.count === 1 ? 'refund' : 'refunds'}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detail table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Detail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            {React.createElement(
              'table',
              { className: 'min-w-full text-xs', 'data-testid': 'cash-drawer-detail-table' },
              React.createElement(
                'thead',
                { className: 'bg-slate-50 dark:bg-slate-900/40' },
                React.createElement(
                  'tr',
                  { key: '__head' },
                  React.createElement('th', { className: 'text-left font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'When'),
                  React.createElement('th', { className: 'text-left font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Student'),
                  React.createElement('th', { className: 'text-left font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Method'),
                  React.createElement('th', { className: 'text-right font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Amount'),
                  React.createElement('th', { className: 'text-left font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Reason'),
                  React.createElement('th', { className: 'text-left font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'By'),
                  React.createElement('th', { className: 'text-left font-mono text-[10px] uppercase tracking-wide px-3 py-2' }, 'Refund ID')
                )
              ),
              React.createElement(
                'tbody',
                { className: 'divide-y divide-slate-100 dark:divide-slate-800' },
                ...((data?.rows || []).length === 0
                  ? [
                      React.createElement(
                        'tr',
                        { key: '__empty' },
                        React.createElement(
                          'td',
                          { colSpan: 7, className: 'px-3 py-6 text-center text-slate-400', 'data-testid': 'cash-drawer-empty' },
                          'No offline refunds in this window. The till should match the books.'
                        )
                      ),
                    ]
                  : data.rows.map((r) =>
                      React.createElement(
                        'tr',
                        { key: r.id, 'data-testid': `cash-drawer-row-${r.id}` },
                        React.createElement('td', { className: 'px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300' }, formatDateTime(r.refund_initiated_at)),
                        React.createElement('td', { className: 'px-3 py-2 font-semibold text-slate-900 dark:text-slate-100' }, r.student_name || '—'),
                        React.createElement('td', { className: 'px-3 py-2' }, METHOD_LABEL[r.refund_offline_method] || r.refund_offline_method || '—'),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-mono tabular-nums' }, formatINR(r.refund_amount)),
                        React.createElement('td', { className: 'px-3 py-2 text-slate-600 dark:text-slate-300 max-w-[220px] truncate', title: r.refund_reason || '' }, r.refund_reason || '—'),
                        React.createElement('td', { className: 'px-3 py-2 text-slate-500' }, r.refund_initiated_by || '—'),
                        React.createElement('td', { className: 'px-3 py-2 font-mono text-[10px] text-slate-400' }, (r.refund_id || '').slice(0, 24))
                      )
                    ))
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
